import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Button } from "components/ui/button";
import { generateMasterAccessUserMagicLink } from "api/masteraccessapi";

/**
 * Master access: list of linked accounts with external "Access" buttons.
 */
const CityAccessListSection = ({
  cityAccessList,
  accessCode,
  cityNameByUsername = {},
}) => {
  const [loadingStates, setLoadingStates] = useState({});
  const [accessErrors, setAccessErrors] = useState({});

  const accountLabelFor = (username) =>
    cityNameByUsername[String(username || "").toLowerCase()] || username || "—";

  const sortedCityAccessList = useMemo(() => {
    if (!cityAccessList?.length) return [];
    return [...cityAccessList].sort((a, b) =>
      accountLabelFor(a?.username).localeCompare(
        accountLabelFor(b?.username),
        undefined,
        {
          sensitivity: "base",
        },
      ),
    );
  }, [cityAccessList, cityNameByUsername]);

  if (!cityAccessList?.length) return null;

  const handleLinkClick = async (item, rowIndex) => {
    if (!item?.username) return;

    const win = window.open("about:blank", "_blank");
    if (!win) {
      alert(
        "Your browser blocked the new tab. Allow pop-ups for this site, then try again.",
      );
      return;
    }

    setLoadingStates((prev) => ({ ...prev, [rowIndex]: true }));
    setAccessErrors((prev) => ({ ...prev, [rowIndex]: null }));

    try {
      let url = null;
      if (accessCode) {
        const result = await generateMasterAccessUserMagicLink(
          accessCode,
          item.username,
        );
        if (result.error || !result.link) {
          win.close();
          setAccessErrors((prev) => ({
            ...prev,
            [rowIndex]: result.error || "Could not open access link.",
          }));
          return;
        }
        url = result.link;
      } else if (item.link) {
        url = item.link;
      } else {
        win.close();
        setAccessErrors((prev) => ({
          ...prev,
          [rowIndex]:
            "Session missing access code. Open the dashboard again from your invite link.",
        }));
        return;
      }

      win.location.href = url;
    } catch (e) {
      win.close();
      setAccessErrors((prev) => ({
        ...prev,
        [rowIndex]: e?.message || "Something went wrong.",
      }));
    } finally {
      setLoadingStates((prev) => ({ ...prev, [rowIndex]: false }));
    }
  };

  return (
    <>
      <p className="font-['Inter']">Your Pro Forma Access</p>
      <div className="mt-2 w-full border bg-white rounded-lg flex flex-col items-center justify-between max-h-[200px] overflow-y-scroll">
        {sortedCityAccessList.map((city, index) => {
          const accountLabel = accountLabelFor(city.username);
          return (
            <div
              key={city.username ?? index}
              className={`flex flex-col w-full ${
                index !== 0 ? "border-t border-gray-200" : ""
              }`}>
              <div className="flex justify-between px-4 py-2 items-center w-full">
                <span className="text-xs text-gray-800">{accountLabel}</span>
                <Button
                  type="button"
                  onClick={() => handleLinkClick(city, index)}
                  className="text-white text-xs rounded-md transition-colors py-0.5 px-2 h-7"
                  disabled={loadingStates[index]}>
                  {loadingStates[index] ? (
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      aria-hidden>
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                  ) : (
                    "Access"
                  )}
                </Button>
              </div>
              {accessErrors[index] ? (
                <p className="px-1 pb-1 text-[10px] text-red-600 leading-tight">
                  {accessErrors[index]}
                </p>
              ) : null}
            </div>
          );
        })}
      </div>
    </>
  );
};

CityAccessListSection.propTypes = {
  cityAccessList: PropTypes.arrayOf(
    PropTypes.shape({
      username: PropTypes.string,
      link: PropTypes.string,
    }),
  ),
  accessCode: PropTypes.string,
  cityNameByUsername: PropTypes.objectOf(PropTypes.string),
};

CityAccessListSection.defaultProps = {
  accessCode: undefined,
  cityNameByUsername: {},
};

export default CityAccessListSection;
